/**
 * SettingsScreen - desktop settings hub.
 *
 * Section rail on the left, content panel on the right.
 *
 * Toggling any Billing setting writes to useSettingsStore which the cart
 * math reads live on the next recompute (see useCartStore.getCartConfig).
 */

import { useFeedbackPopup } from '@/hooks/useFeedbackPopup';
import { buildReceiptHtml } from '@/printing/templates/receiptHtml';
import { getReceiptProfile } from '@/printing/engine/ReceiptProfiles';
import { BILL_DOCUMENT_SETTING, resolveBillDocument, type BillDocument } from '@/printing/billDocument';
import type { PaperWidth } from '@/printing/contracts/PrintTypes';
import { Switch } from '@/components/ui/Switch';
import { useErrorPopup } from '@/hooks/useErrorPopup';
import { useState, useEffect } from 'react';
import { Receipt, Calculator, Printer, FileText, UserCircle, Download, Wifi, Activity, Database, Users, Bell, Building2, Laptop } from 'lucide-react';
import { Q } from '@nozbe/watermelondb';
import { cn } from '@/utils/cn';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useSettingsStore } from '@/store/useSettingsStore';
import { computeLoyaltyPointsEarned, loyaltyRedemptionValue } from '@/billing/billingEngine';
import { useAppUpdater } from '@/hooks/useAppUpdater';
import { useBackendStatus } from '@/hooks/useBackendStatus';
import { appInfo, printer } from '@/platform';
import { changePin } from '@/auth/authApi';
import { describeError } from '@/utils/errorMessage';
import { API_BASE_URL } from '@/config';
import { InvoiceTemplateSection } from './InvoiceTemplateSection';
import { BusinessSection } from './BusinessSection';
import { DevicesSection } from './DevicesSection';
import { NotificationsSection } from './NotificationsSection';
import { database, txn } from '@/db';
import Merchant from '@/db/models/Merchant';
import Store from '@/db/models/Store';
import { Setting } from '@/db/models/System';
import User from '@/db/models/User';
import UserInvitation from '@/db/models/UserInvitation';
import { useAppStore } from '@/store/useAppStore';
import { useObservable } from '@/hooks/useObservable';
import { ROLES, type Role } from '@/rbac';

// WEB ADAPTATION: 'updates', 'backup' and 'diagnostics' are dropped.
// Backup and Diagnostics are built on Electron file-system and app-path APIs
// that no browser provides, and a web app updates by reloading rather than by
// downloading an installer. Showing panes that cannot work would be worse than
// omitting them. A browser-native backup (export/import JSON via the file shim)
// is possible later and would be a different implementation, not a port.
type SectionId = 'business' | 'devices' | 'billing' | 'tax' | 'printing' | 'invoice' | 'account' | 'staff' | 'notifications' | 'connection';

interface SectionDef {
  id: SectionId;
  label: string;
  icon: typeof Receipt;
  ready: boolean;
}

const SECTIONS: readonly SectionDef[] = [
  // First, because it is the store's identity: the supplier block every
  // invoice prints, and the one thing a new merchant must fill in.
  { id: 'business',      label: 'Business',     icon: Building2,  ready: true  },
  { id: 'devices',       label: 'Devices',      icon: Laptop,     ready: true  },
  { id: 'billing',       label: 'Billing',      icon: Receipt,    ready: true  },
  { id: 'tax',           label: 'Tax & GST',    icon: Calculator, ready: true  },
  { id: 'printing',      label: 'Printing',     icon: Printer,    ready: true  },
  { id: 'invoice',       label: 'Invoice',      icon: FileText,   ready: true  },
  { id: 'account',       label: 'Account',      icon: UserCircle, ready: true  },
  { id: 'staff',         label: 'Staff',        icon: Users,      ready: true  },
  { id: 'notifications', label: 'Notifications', icon: Bell,      ready: true  },
  { id: 'connection',    label: 'Connection',    icon: Wifi,      ready: true  },
];

export function SettingsScreen() {
  const [active, setActive] = useState<SectionId>('billing');

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-[220px_1fr] md:gap-6">
      {/* Section rail — a swipeable strip of tabs on phones */}
      <nav className="-mx-1 flex gap-1 overflow-x-auto px-1 pb-1 md:mx-0 md:block md:space-y-1 md:overflow-visible md:px-0 md:pb-0">
        <h1 className="hidden px-2 pb-3 text-display font-semibold text-fg-primary md:block">Settings</h1>
        {SECTIONS.map(({ id, label, icon: Icon, ready }) => (
          <button
            key={id}
            type="button"
            onClick={() => ready && setActive(id)}
            disabled={!ready}
            className={cn(
              'flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-md px-3 py-2 text-left text-body transition-colors duration-120 ease-apple md:w-full',
              !ready && 'cursor-not-allowed text-fg-tertiary',
              ready && active === id && 'bg-accent/10 text-accent-ink',
              ready && active !== id && 'text-fg-secondary hover:bg-bg-subtle hover:text-fg-primary',
            )}
          >
            <Icon size={16} strokeWidth={1.75} />
            <span>{label}</span>
            {!ready && <span className="ml-auto text-label uppercase">soon</span>}
          </button>
        ))}
      </nav>

      {/* Active section content */}
      <section>
        {active === 'business'       && <BusinessSection />}
        {active === 'devices'        && <DevicesSection />}
        {active === 'billing'       && <BillingSection />}
        {active === 'tax'            && <TaxStoreProfileSection />}
        {active === 'printing'       && <PrintingSection />}
        {active === 'invoice'        && <InvoiceTemplateSection />}
        {active === 'account'        && <AccountSection />}
        {active === 'staff'          && <StaffSection />}
        {active === 'notifications'  && <NotificationsSection />}
        {active === 'connection'     && <ConnectionSection />}
      </section>
    </div>
  );
}

// ─── Billing section ─────────────────────────────────────────────────────────

const ROUNDING_OPTIONS: ReadonlyArray<{ value: string; label: string; hint: string }> = [
  { value: 'NONE',    label: 'No rounding', hint: 'Bill total = computed total to paisa precision.' },
  { value: 'NEAREST', label: 'Nearest ₹1',  hint: '49.50 → 50, 49.40 → 49.' },
  { value: 'UP',      label: 'Always up',   hint: '49.10 → 50.' },
  { value: 'DOWN',    label: 'Always down', hint: '49.90 → 49.' },
];

function BillingSection() {
  const settings = useSettingsStore((s) => s.settings);
  const setSetting = useSettingsStore((s) => s.setSetting);

  const taxInclusive = (settings.TAX_INCLUSIVE ?? 'false') === 'true';
  const priceInclDefault = (settings.PRODUCT_PRICE_INCLUDES_GST ?? 'true') === 'true';
  const composition  = (settings.COMPOSITION_GST ?? 'false') === 'true';
  const roundingMode = settings.ROUNDING_MODE ?? 'NONE';
  const allowManualItems = (settings.ALLOW_MANUAL_BILL_ITEMS ?? 'false') === 'true';

  // Show the shopkeeper the rate in the terms they think in — a percentage of
  // the bill — rather than leaving them to multiply two settings in their head.
  // Desktop shipped at 100% for its whole life because nobody did that sum.
  const loyaltyCfg = {
    rupeesPerPoint: Number(settings.LOYALTY_RUPEES_PER_POINT ?? '100') || 0,
    pointValueRupees: Number(settings.LOYALTY_POINT_VALUE ?? '1') || 0,
  };
  const loyaltyPointsOnThousand = computeLoyaltyPointsEarned(1000, loyaltyCfg);
  const loyaltyValueOnThousand = loyaltyRedemptionValue(loyaltyPointsOnThousand, loyaltyCfg);
  const loyaltyRatePercent = loyaltyCfg.rupeesPerPoint > 0
    ? (loyaltyValueOnThousand / 1000) * 100
    : null;

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <div className="mb-4">
          <h2 className="text-heading font-medium text-fg-primary">Pricing & Tax</h2>
          <p className="text-label text-fg-secondary">
            Changes here take effect on the very next cart computation. Existing held bills are not retroactively recalculated.
          </p>
        </div>

        <ToggleRow
          label="Tax-inclusive pricing"
          description="Selling prices already include GST; tax is reverse-calculated per Section 15."
          checked={taxInclusive}
          onChange={(v) => setSetting('TAX_INCLUSIVE', v ? 'true' : 'false')}
        />

        <ToggleRow
          label="New products priced with GST"
          description="Default the “price includes GST” choice when adding or importing products. Prices are still normalised to your billing convention on save."
          checked={priceInclDefault}
          onChange={(v) => setSetting('PRODUCT_PRICE_INCLUDES_GST', v ? 'true' : 'false')}
        />

        <ToggleRow
          label="Composition dealer"
          description="Suppress GST on invoices for merchants registered under the composition scheme."
          checked={composition}
          onChange={(v) => setSetting('COMPOSITION_GST', v ? 'true' : 'false')}
        />

        {/* Store-scoped, unlike everything above it — one branch may sell
            off-catalog while another keeps a closed inventory. */}
        <ToggleRow
          label="Allow manual bill entries"
          description="Let this store bill an item that is not in its inventory. Type the name in the billing search box; if nothing matches, you are asked for the HSN, rate and quantity. Applies to this store only."
          checked={allowManualItems}
          onChange={(v) => setSetting('ALLOW_MANUAL_BILL_ITEMS', v ? 'true' : 'false')}
        />
      </Card>

      <Card className="p-5">
        <div className="mb-4">
          <h2 className="text-heading font-medium text-fg-primary">Loyalty points</h2>
          <p className="text-label text-fg-secondary">
            Your call, not ours. Set what a customer earns and what a point is worth.
            The default is 1 point per ₹100 spent, each worth ₹1 — a 1% reward.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-label uppercase text-fg-tertiary">Spend per point (₹)</span>
            <input
              type="number"
              min={0}
              step={1}
              value={settings.LOYALTY_RUPEES_PER_POINT ?? '100'}
              onChange={(e) => setSetting('LOYALTY_RUPEES_PER_POINT', e.target.value)}
              className="rounded-md border border-stroke-hairline/60 bg-bg-surface px-3 py-2 text-body tabular-nums text-fg-primary"
            />
            <span className="text-label text-fg-tertiary">0 turns earning off.</span>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-label uppercase text-fg-tertiary">Point is worth (₹)</span>
            <input
              type="number"
              min={0}
              step={0.01}
              value={settings.LOYALTY_POINT_VALUE ?? '1'}
              onChange={(e) => setSetting('LOYALTY_POINT_VALUE', e.target.value)}
              className="rounded-md border border-stroke-hairline/60 bg-bg-surface px-3 py-2 text-body tabular-nums text-fg-primary"
            />
            <span className="text-label text-fg-tertiary">Discount given per point redeemed.</span>
          </label>
        </div>

        <div className="mt-4 rounded-md border border-stroke-hairline/40 bg-bg-subtle px-3 py-2.5">
          <div className="text-label uppercase text-fg-tertiary">What this costs you</div>
          <div className="mt-0.5 text-body text-fg-primary tabular-nums">
            {loyaltyRatePercent === null
              ? 'Loyalty earning is off.'
              : `A ₹1,000 sale earns ${loyaltyPointsOnThousand} points, worth ₹${loyaltyValueOnThousand.toFixed(2)} — a ${loyaltyRatePercent.toFixed(2)}% reward.`}
          </div>
        </div>
      </Card>

      <Card className="p-5">
        <div className="mb-4">
          <h2 className="text-heading font-medium text-fg-primary">Rounding mode</h2>
          <p className="text-label text-fg-secondary">
            Applied to the invoice total only. Line-item paisa precision is always preserved.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {ROUNDING_OPTIONS.map((opt) => {
            const active = roundingMode === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSetting('ROUNDING_MODE', opt.value)}
                className={cn(
                  'flex flex-col gap-0.5 rounded-md border px-3 py-2.5 text-left transition-colors duration-120 ease-apple',
                  active
                    ? 'border-accent/40 bg-accent/5 text-fg-primary'
                    : 'border-stroke-hairline/60 bg-bg-surface text-fg-secondary hover:border-stroke-hairline hover:text-fg-primary',
                )}
              >
                <span className="text-body font-medium">{opt.label}</span>
                <span className="text-label text-fg-tertiary">{opt.hint}</span>
              </button>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

// ─── ToggleRow helper ────────────────────────────────────────────────────────

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

const INDIAN_STATES = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Delhi',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jammu and Kashmir',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Andaman and Nicobar Islands',
  'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Ladakh',
  'Lakshadweep',
  'Puducherry',
] as const;

function cleanStatus(merchantGstin: string, gstinOverride: string): boolean {
  return Boolean(merchantGstin.trim() || gstinOverride.trim());
}

interface ToggleRowProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}

function ToggleRow({ label, description, checked, onChange }: ToggleRowProps) {
  return (
    <div className="flex items-start justify-between gap-4 border-t border-stroke-hairline/40 py-3 first:border-t-0 first:pt-0">
      <div className="min-w-0 flex-1">
        <div className="text-body font-medium text-fg-primary">{label}</div>
        <div className="text-label text-fg-secondary">{description}</div>
      </div>
      <Switch checked={checked} onChange={onChange} label={label} />
    </div>
  );
}

// ─── Updates section ─────────────────────────────────────────────────────────

function TaxStoreProfileSection() {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const settings = useSettingsStore((s) => s.settings);
  const hydrateFromDB = useSettingsStore((s) => s.hydrateFromDB);
  const [store, setStore] = useState<Store | null>(null);
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  useFeedbackPopup(message, 'success', () => setMessage(null));
  const [error, setError] = useState<string | null>(null);
  useErrorPopup(error, () => setError(null));

  const [storeName, setStoreName] = useState('');
  const [storePhone, setStorePhone] = useState('');
  const [storeAddress, setStoreAddress] = useState('');
  const [storeCity, setStoreCity] = useState('');
  const [storeState, setStoreState] = useState('');
  const [storePincode, setStorePincode] = useState('');
  const [merchantEmail, setMerchantEmail] = useState('');
  const [merchantPan, setMerchantPan] = useState('');
  const [merchantGstin, setMerchantGstin] = useState('');
  const [gstinOverride, setGstinOverride] = useState('');
  const [invoicePrefix, setInvoicePrefix] = useState('');
  const [taxInclusive, setTaxInclusive] = useState((settings.TAX_INCLUSIVE ?? 'false') === 'true');
  const [compositionGst, setCompositionGst] = useState((settings.COMPOSITION_GST ?? 'false') === 'true');
  const [roundingMode, setRoundingMode] = useState(settings.ROUNDING_MODE ?? 'NONE');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      setMessage(null);
      if (!activeStoreId) {
        setStore(null);
        setMerchant(null);
        setLoading(false);
        return;
      }
      try {
        const nextStore = await database.get<Store>('stores').find(activeStoreId);
        const nextMerchant = await nextStore.merchant.fetch() as Merchant;
        const dbSettings = await database.get<Setting>('settings')
          .query(Q.where('merchant_id', nextMerchant.id), Q.where('is_deleted', false))
          .fetch();
        if (cancelled) return;
        setStore(nextStore);
        setMerchant(nextMerchant);
        setStoreName(nextStore.name ?? '');
        setStorePhone(nextStore.phone ?? nextMerchant.phone ?? '');
        setStoreAddress(nextStore.address ?? nextMerchant.address ?? '');
        setStoreCity(nextStore.city ?? nextMerchant.city ?? '');
        setStoreState(nextStore.state ?? nextMerchant.state ?? '');
        setStorePincode(nextStore.pincode ?? nextMerchant.pincode ?? '');
        setMerchantEmail(nextMerchant.email ?? '');
        setMerchantPan(nextMerchant.pan ?? '');
        setMerchantGstin(nextMerchant.gstin ?? '');
        setGstinOverride(nextStore.gstinOverride ?? '');
        setInvoicePrefix(nextStore.invoicePrefix ?? '');

        const getVal = (key: string) => dbSettings.find((row) => row.settingKey === key)?.settingValue ?? settings[key] ?? '';
        setTaxInclusive(getVal('TAX_INCLUSIVE') === 'true');
        setCompositionGst(getVal('COMPOSITION_GST') === 'true');
        setRoundingMode(getVal('ROUNDING_MODE') || 'NONE');
      } catch {
        if (!cancelled) setError('Unable to load store tax profile.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [activeStoreId]);

  async function save() {
    if (!store || !merchant) return;
    setError(null);
    setMessage(null);

    const cleanMerchantGstin = merchantGstin.trim().toUpperCase();
    const cleanStoreGstin = gstinOverride.trim().toUpperCase();
    const cleanPan = merchantPan.trim().toUpperCase();
    const cleanPhone = storePhone.replace(/\D/g, '');
    const cleanPincode = storePincode.replace(/\D/g, '');
    const cleanPrefix = invoicePrefix.trim().toUpperCase();

    if (!storeName.trim()) {
      setError('Store name is required.');
      return;
    }
    if (cleanMerchantGstin && !GSTIN_RE.test(cleanMerchantGstin)) {
      setError('Merchant GSTIN must be a valid 15 character GSTIN.');
      return;
    }
    if (cleanStoreGstin && !GSTIN_RE.test(cleanStoreGstin)) {
      setError('Store GSTIN override must be a valid 15 character GSTIN.');
      return;
    }
    if (cleanPan && !PAN_RE.test(cleanPan)) {
      setError('PAN must be a valid 10 character PAN.');
      return;
    }
    if (cleanPhone && cleanPhone.length !== 10) {
      setError('Store phone must be 10 digits.');
      return;
    }
    if (cleanPincode && cleanPincode.length !== 6) {
      setError('Pincode must be 6 digits.');
      return;
    }
    if (cleanPrefix && !/^[A-Z0-9/-]{1,12}$/.test(cleanPrefix)) {
      setError('Invoice prefix can use A-Z, 0-9, slash, or dash, up to 12 characters.');
      return;
    }

    setSaving(true);
    try {
      await txn(async (db) => {
        await merchant.update((record) => {
          record.email = merchantEmail.trim() || null;
          record.pan = cleanPan || null;
          record.gstin = cleanMerchantGstin || null;
          record.version = merchant.version + 1;
          record.isSynced = false;
        });
        await store.update((record) => {
          record.name = storeName.trim();
          record.phone = cleanPhone || null;
          record.address = storeAddress.trim() || null;
          record.city = storeCity.trim() || null;
          record.state = storeState.trim() || null;
          record.pincode = cleanPincode || null;
          record.gstinOverride = cleanStoreGstin || null;
          record.invoicePrefix = cleanPrefix || null;
          record.version = store.version + 1;
          record.isSynced = false;
        });

        const upsertSetting = async (key: string, value: string) => {
          const existing = await db.get<Setting>('settings')
            .query(Q.where('merchant_id', merchant.id), Q.where('setting_key', key), Q.where('is_deleted', false))
            .fetch();
          if (existing[0]) {
            await existing[0].update((record) => {
              record.settingValue = value;
              record.version = existing[0].version + 1;
              record.isSynced = false;
            });
          } else {
            await db.get<Setting>('settings').create((record) => {
              record.storeId = null;
              record.merchantId = merchant.id;
              record.category = 'billing';
              record.settingKey = key;
              record.settingValue = value;
              record.version = 1;
              record.isSynced = false;
              record.isDeleted = false;
            });
          }
        };

        await upsertSetting('TAX_INCLUSIVE', taxInclusive ? 'true' : 'false');
        await upsertSetting('COMPOSITION_GST', compositionGst ? 'true' : 'false');
        await upsertSetting('ROUNDING_MODE', roundingMode);
      });

      const freshSettings = await database.get<Setting>('settings')
        .query(Q.where('merchant_id', merchant.id), Q.where('is_deleted', false))
        .fetch();
      hydrateFromDB(freshSettings.map((row) => ({ settingKey: row.settingKey, settingValue: row.settingValue })));
      const freshStore = await database.get<Store>('stores').find(store.id);
      const freshMerchant = await freshStore.merchant.fetch() as Merchant;
      setStore(freshStore);
      setMerchant(freshMerchant);
      setMessage('Tax and store profile saved.');
    } catch {
      setError('Unable to save tax and store profile.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <Card className="p-6 text-fg-secondary">Loading tax profile...</Card>;
  }

  if (!store || !merchant) {
    return <Card className="p-6 text-fg-secondary">Select a store before editing tax settings.</Card>;
  }

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-heading font-medium text-fg-primary">Tax & GST</h2>
            <p className="text-label text-fg-secondary">Store identity, GST registration, invoice numbering, and billing tax behavior.</p>
          </div>
          <span className={cn(
            'rounded-full px-2.5 py-0.5 text-label font-medium',
            cleanStatus(merchantGstin, gstinOverride) ? 'bg-success/10 text-success-ink' : 'bg-warning/10 text-warning-ink',
          )}>
            {cleanStatus(merchantGstin, gstinOverride) ? 'GST ready' : 'GSTIN missing'}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label><span className="mb-1 block text-label uppercase text-fg-tertiary">Merchant GSTIN</span><Input value={merchantGstin} onChange={(e) => setMerchantGstin(e.target.value.toUpperCase().slice(0, 15))} placeholder="27ABCDE1234F1Z5" /></label>
          <label><span className="mb-1 block text-label uppercase text-fg-tertiary">Store GSTIN Override</span><Input value={gstinOverride} onChange={(e) => setGstinOverride(e.target.value.toUpperCase().slice(0, 15))} placeholder="Optional" /></label>
          <label><span className="mb-1 block text-label uppercase text-fg-tertiary">PAN</span><Input value={merchantPan} onChange={(e) => setMerchantPan(e.target.value.toUpperCase().slice(0, 10))} /></label>
          <label><span className="mb-1 block text-label uppercase text-fg-tertiary">Invoice Prefix</span><Input value={invoicePrefix} onChange={(e) => setInvoicePrefix(e.target.value.toUpperCase().slice(0, 12))} placeholder="INV-" /></label>
        </div>
      </Card>

      <Card className="p-5">
        <div className="mb-4">
          <h2 className="text-heading font-medium text-fg-primary">Store Profile</h2>
          <p className="text-label text-fg-secondary">This address appears on receipts, invoices, and GST reports.</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label><span className="mb-1 block text-label uppercase text-fg-tertiary">Store Name</span><Input value={storeName} onChange={(e) => setStoreName(e.target.value)} /></label>
          <label><span className="mb-1 block text-label uppercase text-fg-tertiary">Store Phone</span><Input value={storePhone} onChange={(e) => setStorePhone(e.target.value.replace(/\D/g, '').slice(0, 10))} /></label>
          <label className="col-span-2"><span className="mb-1 block text-label uppercase text-fg-tertiary">Address</span><Input value={storeAddress} onChange={(e) => setStoreAddress(e.target.value)} /></label>
          <label><span className="mb-1 block text-label uppercase text-fg-tertiary">City</span><Input value={storeCity} onChange={(e) => setStoreCity(e.target.value)} /></label>
          <label>
            <span className="mb-1 block text-label uppercase text-fg-tertiary">Place of Supply State</span>
            <select value={storeState} onChange={(e) => setStoreState(e.target.value)} className="h-9 w-full rounded-md border border-stroke-hairline/20 bg-bg-surface px-3 text-body text-fg-primary">
              <option value="">Select state</option>
              {INDIAN_STATES.map((state) => <option key={state} value={state}>{state}</option>)}
            </select>
          </label>
          <label><span className="mb-1 block text-label uppercase text-fg-tertiary">Pincode</span><Input value={storePincode} onChange={(e) => setStorePincode(e.target.value.replace(/\D/g, '').slice(0, 6))} /></label>
          <label><span className="mb-1 block text-label uppercase text-fg-tertiary">Merchant Email</span><Input value={merchantEmail} onChange={(e) => setMerchantEmail(e.target.value)} /></label>
        </div>
      </Card>

      <Card className="p-5">
        <div className="mb-4">
          <h2 className="text-heading font-medium text-fg-primary">Billing Tax Rules</h2>
          <p className="text-label text-fg-secondary">Saved to shared settings so billing math changes immediately after save.</p>
        </div>
        <ToggleRow
          label="Tax-inclusive pricing"
          description="Selling prices already include GST; tax is reverse-calculated from the customer-facing price."
          checked={taxInclusive}
          onChange={setTaxInclusive}
        />
        <ToggleRow
          label="Composition dealer"
          description="Suppress GST collection on invoices for composition-registered merchants."
          checked={compositionGst}
          onChange={setCompositionGst}
        />
        <div className="mt-4 grid grid-cols-2 gap-2">
          {ROUNDING_OPTIONS.map((opt) => {
            const active = roundingMode === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setRoundingMode(opt.value)}
                className={cn(
                  'flex flex-col gap-0.5 rounded-md border px-3 py-2.5 text-left transition-colors duration-120 ease-apple',
                  active
                    ? 'border-accent/40 bg-accent/5 text-fg-primary'
                    : 'border-stroke-hairline/60 bg-bg-surface text-fg-secondary hover:border-stroke-hairline hover:text-fg-primary',
                )}
              >
                <span className="text-body font-medium">{opt.label}</span>
                <span className="text-label text-fg-tertiary">{opt.hint}</span>
              </button>
            );
          })}
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => void save()} disabled={saving}>{saving ? 'Saving...' : 'Save Tax & Store Profile'}</Button>
      </div>
    </div>
  );
}

function PrintingSection() {
  const settings = useSettingsStore((s) => s.settings);
  const setSetting = useSettingsStore((s) => s.setSetting);
  const autoPrint = (settings.THERMAL_AUTO_PRINT_ON_SALE ?? 'false') === 'true';
  const billDoc = resolveBillDocument(settings[BILL_DOCUMENT_SETTING]);
  const paperWidth = (Number(settings.THERMAL_PRINTER_PAPER_WIDTH) === 58 || Number(settings.THERMAL_PRINTER_PAPER_WIDTH) === 112
    ? Number(settings.THERMAL_PRINTER_PAPER_WIDTH)
    : 80) as PaperWidth;
  const [test, setTest] = useState<{ ok: boolean; message: string } | null>(null);
  useFeedbackPopup(test?.message, test?.ok ? 'success' : 'error', () => setTest(null));

  // This page used to be Desktop's: a network printer's IP and port, and a
  // list of Windows printers. A browser can do neither — it cannot open a
  // socket, and it is never told which printers exist — so every one of those
  // controls saved a value that nothing could use. What a browser CAN do is
  // print a page, to any printer the computer has a driver for.
  async function testPrint(kind: BillDocument) {
    setTest(null);
    const line = '-'.repeat(getReceiptProfile(paperWidth).charsPerLine);
    const html = kind === 'thermal'
      ? buildReceiptHtml(['BigxBook', 'Receipt printer test', line, new Date().toLocaleString(), 'If this fits the roll, you are set.', line].join('\n'), paperWidth)
      : '<!doctype html><html><body style="font-family:sans-serif;padding:24px;font-size:14px">' +
        '<h2 style="margin:0 0 8px">BigxBook — A4 printer test</h2>' +
        `<p>${new Date().toLocaleString()}</p><p>If you can read this, A4 printing works.</p></body></html>`;
    const res = await printer.printHtml({ html });
    setTest({ ok: res.ok, message: res.ok ? 'Test page sent to the print dialog.' : res.message || 'Print failed.' });
  }

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <div className="mb-4">
          <h2 className="text-heading font-medium text-fg-primary">Printing</h2>
          <p className="text-label text-fg-secondary">
            Bills print through your browser&apos;s print dialog. Pick your receipt or office printer
            there once — the browser remembers it. Any printer with a driver installed on this computer
            works, USB or network.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-stroke-hairline/15 bg-bg-subtle px-3 py-2.5">
          <div className="text-body text-fg-primary">
            Your bill: <span className="font-medium">{billDoc === 'a4' ? 'A4 tax invoice' : `Counter receipt · ${paperWidth} mm`}</span>
          </div>
          <span className="text-label text-fg-tertiary">Change it, and the paper width, under Invoice.</span>
        </div>

        <div className="mt-4">
          <ToggleRow
            label="Print after each sale"
            description="Opens the print dialog with your bill as soon as a sale is saved."
            checked={autoPrint}
            onChange={(v) => setSetting('THERMAL_AUTO_PRINT_ON_SALE', v ? 'true' : 'false')}
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-body text-fg-tertiary">Print a test page to check the printer and paper.</div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => void testPrint('thermal')}>Test receipt</Button>
            <Button variant="secondary" onClick={() => void testPrint('a4')}>Test A4</Button>
          </div>
        </div>
      </Card>

      <Card className="p-5">
        <h2 className="text-heading font-medium text-fg-primary">Skip the print dialog</h2>
        <p className="mt-1 text-body text-fg-secondary">
          On a till used only for billing, start Chrome with <code className="rounded bg-bg-subtle px-1">--kiosk-printing</code> and
          bills go straight to the default printer with no dialog. Set the receipt printer as the
          computer&apos;s default printer first.
        </p>
      </Card>
    </div>
  );
}

function AccountSection() {
  const currentUser = useAppStore((s) => s.currentUser);
  const setCurrentUser = useAppStore((s) => s.setCurrentUser);
  const [name, setName] = useState(currentUser?.name ?? '');
  const [email, setEmail] = useState('');
  // Required to change an existing PIN. A valid session is not enough on its
  // own: a token can be a session left open on a shared counter, and changing
  // the PIN from one is how a till gets taken over.
  const [currentPin, setCurrentPin] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  useFeedbackPopup(message, 'success', () => setMessage(null));
  const [error, setError] = useState<string | null>(null);
  useErrorPopup(error, () => setError(null));

  useEffect(() => {
    if (!currentUser?.id) return;
    database.get<User>('users').find(currentUser.id).then((user) => {
      setName(user.name ?? '');
      setEmail(user.email ?? '');
      setPin('');
      setConfirmPin('');
    }).catch(() => undefined);
  }, [currentUser?.id]);

  async function save() {
    if (!currentUser?.id) return;
    setError(null);
    setMessage(null);
    // The PIN is a CREDENTIAL and is changed through the API, never written
    // here.
    //
    // This used to do `record.pin = pin` — the raw digits — into a fully
    // replicated column, so the plaintext reached the server and every other
    // device on the merchant. That was survivable only while nothing
    // authenticated against it. Now that you log in with it, the server holds
    // a bcrypt hash and strips the column from sync in both directions, so a
    // local write would be silently discarded: the screen would report success
    // and the PIN would never change.
    if (pin || confirmPin) {
      if (!/^(\d{4}|\d{6})$/.test(pin)) {
        setError('PIN must be exactly 4 or 6 digits.');
        return;
      }
      if (pin !== confirmPin) {
        setError('PIN confirmation does not match.');
        return;
      }
      try {
        await changePin(currentPin || undefined, pin);
      } catch (err) {
        setError(describeError(err));
        return;
      }
    }

    const user = await database.get<User>('users').find(currentUser.id);
    await txn(async (db) => {
      await user.update((record) => {
        record.name = name.trim() || null;
        record.email = email.trim() || null;
        record.version = user.version + 1;
        record.isSynced = false;
      });
    });
    await setCurrentUser({ ...currentUser, name: name.trim() || undefined });
    setPin('');
    setConfirmPin('');
    setCurrentPin('');
    setMessage(pin ? 'Account saved and PIN updated.' : 'Account saved.');
  }

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <div className="mb-4">
          <h2 className="text-heading font-medium text-fg-primary">My Account</h2>
          <p className="text-label text-fg-secondary">Local profile details sync with the backend.</p>
        </div>
        <div className="grid max-w-xl grid-cols-2 gap-3">
          <label><span className="mb-1 block text-label uppercase text-fg-tertiary">Name</span><Input value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label><span className="mb-1 block text-label uppercase text-fg-tertiary">Email</span><Input value={email} onChange={(e) => setEmail(e.target.value)} /></label>
          <label><span className="mb-1 block text-label uppercase text-fg-tertiary">Current PIN</span><Input value={currentPin} onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, '').slice(0, 6))} type="password" inputMode="numeric" placeholder="Blank if you have none yet" /></label>
          <label><span className="mb-1 block text-label uppercase text-fg-tertiary">New PIN</span><Input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))} type="password" inputMode="numeric" placeholder="4 or 6 digits" /></label>
          <label><span className="mb-1 block text-label uppercase text-fg-tertiary">Confirm PIN</span><Input value={confirmPin} onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))} type="password" inputMode="numeric" /></label>
        </div>
        <Button className="mt-4" onClick={() => void save()}>Save account</Button>
      </Card>
    </div>
  );
}

function StaffSection() {
  const currentUser = useAppStore((s) => s.currentUser);
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const merchantId = currentUser?.merchantId;
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<Role>(ROLES.CASHIER);
  const [staffMessage, setStaffMessage] = useState<string | null>(null);
  useFeedbackPopup(staffMessage, 'success', () => setStaffMessage(null));
  const [staffError, setStaffError] = useState<string | null>(null);
  useErrorPopup(staffError, () => setStaffError(null));
  const users = useObservable<User[]>(
    () => merchantId ? database.get<User>('users').query(Q.where('merchant_id', merchantId), Q.where('is_deleted', false)).observe() : null,
    [merchantId],
    [],
  );
  const invitations = useObservable<UserInvitation[]>(
    () => merchantId ? database.get<UserInvitation>('user_invitations').query(Q.where('merchant_id', merchantId), Q.where('is_deleted', false), Q.sortBy('created_at', Q.desc)).observe() : null,
    [merchantId],
    [],
  );
  const inviteRoles = [ROLES.MANAGER, ROLES.CASHIER, ROLES.ACCOUNTANT, ROLES.INVENTORY_MANAGER, ROLES.VIEWER] as const;
  const activeUsers = users.filter((user) => user.isActive);
  const pendingInvitations = invitations.filter((invitation) => invitation.status === 'PENDING');

  async function invite() {
    setStaffError(null);
    setStaffMessage(null);
    const normalizedPhone = phone.replace(/\D/g, '');
    const trimmedName = name.trim();
    if (!merchantId || !activeStoreId || !currentUser?.id) return;
    if (!trimmedName) {
      setStaffError('Staff name is required.');
      return;
    }
    if (!/^\d{10}$/.test(normalizedPhone)) {
      setStaffError('Enter a valid 10 digit phone number.');
      return;
    }
    if (currentUser.phone === normalizedPhone) {
      setStaffError('You cannot invite your own phone number.');
      return;
    }
    if (users.some((user) => user.phone === normalizedPhone && user.isActive)) {
      setStaffError('That phone number already belongs to active staff.');
      return;
    }
    if (pendingInvitations.some((invitation) => invitation.phone === normalizedPhone)) {
      setStaffError('A pending invite already exists for this phone number.');
      return;
    }
    await txn(async (db) => {
      await db.get<UserInvitation>('user_invitations').create((record) => {
        record.merchantId = merchantId;
        record.storeId = activeStoreId;
        record.phone = normalizedPhone;
        record.name = trimmedName;
        record.role = role;
        record.permissions = null;
        record.status = 'PENDING';
        record.invitedById = currentUser.id;
        record.expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
        record.requiresApproval = false;
        record.version = 1;
        record.isSynced = false;
        record.isDeleted = false;
      });
    });
    setName('');
    setPhone('');
    setRole(ROLES.CASHIER);
    setStaffMessage('Invitation created.');
  }

  async function updateUserRole(user: User, nextRole: Role) {
    if (user.id === currentUser?.id || user.isPrimaryOwner || user.role === nextRole) return;
    setStaffError(null);
    setStaffMessage(null);
    await txn(async (db) => {
      await user.update((record) => {
        record.role = nextRole;
        record.version = user.version + 1;
        record.isSynced = false;
      });
    });
    setStaffMessage('Staff role updated.');
  }

  async function toggleUserActive(user: User) {
    if (user.id === currentUser?.id || user.isPrimaryOwner) return;
    setStaffError(null);
    setStaffMessage(null);
    await txn(async (db) => {
      await user.update((record) => {
        record.isActive = !user.isActive;
        record.version = user.version + 1;
        record.isSynced = false;
      });
    });
    setStaffMessage(user.isActive ? 'Staff account disabled.' : 'Staff account enabled.');
  }

  async function revoke(invitation: UserInvitation) {
    setStaffError(null);
    setStaffMessage(null);
    await txn(async (db) => {
      await invitation.update((record) => {
        record.status = 'REVOKED';
        record.version = invitation.version + 1;
        record.isSynced = false;
      });
    });
    setStaffMessage('Invitation revoked.');
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
      <Card className="p-5">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-heading font-medium text-fg-primary">Staff</h2>
            <p className="text-label text-fg-secondary">{activeUsers.length} active, {pendingInvitations.length} pending invite{pendingInvitations.length === 1 ? '' : 's'}</p>
          </div>
        </div>
        <div className="divide-y divide-stroke-hairline/10">
          {users.map((user) => (
            <div key={user.id} className="grid grid-cols-1 items-center gap-2 py-2.5 sm:grid-cols-[1fr_180px_120px] sm:gap-3">
              <div>
                <div className="text-body font-medium text-fg-primary">{user.name || user.phone}</div>
                <div className="text-label uppercase text-fg-tertiary">{user.phone}{user.id === currentUser?.id ? ' - You' : ''}</div>
              </div>
              <select
                value={user.role}
                onChange={(event) => void updateUserRole(user, event.target.value as Role)}
                disabled={user.id === currentUser?.id || user.isPrimaryOwner}
                className="h-9 rounded-md border border-stroke-hairline/20 bg-bg-surface px-3 text-body disabled:opacity-60"
              >
                {Object.values(ROLES).map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <div className="flex items-center justify-end gap-2">
                <span className={user.isActive ? 'text-label text-success-ink' : 'text-label text-warning-ink'}>{user.isActive ? 'Active' : 'Inactive'}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void toggleUserActive(user)}
                  disabled={user.id === currentUser?.id || user.isPrimaryOwner}
                >
                  {user.isActive ? 'Disable' : 'Enable'}
                </Button>
              </div>
            </div>
          ))}
          {users.length === 0 && <div className="text-body text-fg-tertiary">No staff accounts.</div>}
        </div>
      </Card>
      <div className="space-y-6">
        <Card className="p-5">
          <h2 className="mb-4 text-heading font-medium text-fg-primary">Invite Staff</h2>
          <div className="space-y-3">
            <label><span className="mb-1 block text-label uppercase text-fg-tertiary">Name</span><Input value={name} onChange={(e) => setName(e.target.value)} /></label>
            <label><span className="mb-1 block text-label uppercase text-fg-tertiary">Phone</span><Input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} /></label>
            <label>
              <span className="mb-1 block text-label uppercase text-fg-tertiary">Role</span>
              <select value={role} onChange={(e) => setRole(e.target.value as Role)} className="h-9 w-full rounded-md border border-stroke-hairline/20 bg-bg-surface px-3 text-body">
                {inviteRoles.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
            <Button onClick={() => void invite()}>Send invite</Button>
          </div>
        </Card>
        <Card className="p-5">
          <h2 className="mb-4 text-heading font-medium text-fg-primary">Invitations</h2>
          <div className="divide-y divide-stroke-hairline/10">
            {invitations.map((invitation) => (
              <div key={invitation.id} className="flex items-center justify-between py-2.5">
                <div>
                  <div className="text-body text-fg-primary">{invitation.name || invitation.phone}</div>
                  <div className="text-label uppercase text-fg-tertiary">{invitation.role} - {invitation.status}</div>
                  <div className="text-label text-fg-tertiary">Expires {invitation.expiresAt ? invitation.expiresAt.toLocaleDateString() : 'not set'}</div>
                </div>
                {invitation.status === 'PENDING' && <Button variant="ghost" size="sm" onClick={() => void revoke(invitation)}>Revoke</Button>}
              </div>
            ))}
            {invitations.length === 0 && <div className="text-body text-fg-tertiary">No invitations.</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}

function UpdatesSection() {
  const {
    status,
    updateInfo,
    downloadProgress,
    errorMessage,
    check,
    download,
    install,
  } = useAppUpdater();

  const [appVersion, setAppVersion] = useState('');
  useEffect(() => {
    appInfo.getVersion().then(setAppVersion).catch(() => undefined);
  }, []);

  const statusLabel: Record<typeof status, string> = {
    idle:        'Up to date',
    checking:    'Checking for updates…',
    'up-to-date': 'Up to date',
    available:   `v${updateInfo?.version ?? '—'} available`,
    downloading: `Downloading v${updateInfo?.version ?? '—'}`,
    ready:       `v${updateInfo?.version ?? '—'} ready to install`,
    error:       'Update error',
  };

  const statusColor: Partial<Record<typeof status, string>> = {
    available:   'text-accent-ink',
    downloading: 'text-fg-secondary',
    ready:       'text-green-600',
    error:       'text-red-500',
  };

  return (
    <div className="space-y-6">
      {/* Current version + status */}
      <Card className="p-5">
        <div className="mb-4">
          <h2 className="text-heading font-medium text-fg-primary">App Updates</h2>
          <p className="text-label text-fg-secondary">
            Updates are distributed via the auto-update channel and installed on restart.
          </p>
        </div>

        <div className="flex items-center justify-between border-t border-stroke-hairline/40 py-3">
          <div>
            <div className="text-body font-medium text-fg-primary">Current version</div>
            <div className="text-label text-fg-secondary tabular-nums">
              {appVersion ? `v${appVersion}` : '—'}
            </div>
          </div>
          <div className="text-right">
            <div className={cn('text-body font-medium', statusColor[status] ?? 'text-fg-primary')}>
              {statusLabel[status]}
            </div>
            {status === 'error' && errorMessage && (
              <div className="text-label text-red-400 max-w-xs text-right">{errorMessage}</div>
            )}
          </div>
        </div>

        {/* Download progress bar */}
        {status === 'downloading' && downloadProgress !== null && (
          <div className="border-t border-stroke-hairline/40 pt-3">
            <div className="mb-1 flex items-center justify-between text-label text-fg-secondary">
              <span>Downloading…</span>
              <span className="tabular-nums">{Math.round(downloadProgress.percent ?? 0)}%</span>
            </div>
            <div className="h-2 w-full rounded-full bg-bg-subtle overflow-hidden">
              <div
                className="h-full rounded-full bg-accent transition-all duration-200"
                style={{ width: `${downloadProgress.percent ?? 0}%` }}
              />
            </div>
            {downloadProgress.bytesPerSecond > 0 && (
              <div className="mt-1 text-label text-fg-tertiary">
                {formatBytes(downloadProgress.transferred)} / {formatBytes(downloadProgress.total)}
                {' · '}
                {formatBytes(downloadProgress.bytesPerSecond)}/s
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Action card */}
      <Card className="p-5 flex items-center justify-between gap-4">
        <div>
          {status === 'available' && (
            <>
              <div className="text-body font-medium text-fg-primary">
                v{updateInfo?.version} is available
              </div>
              {updateInfo?.releaseNotes && (
                <div className="mt-0.5 text-label text-fg-secondary line-clamp-2">
                  {typeof updateInfo.releaseNotes === 'string'
                    ? updateInfo.releaseNotes
                    : 'Release notes available.'}
                </div>
              )}
            </>
          )}
          {status === 'ready' && (
            <div className="text-body font-medium text-fg-primary">
              Restart to finish installing v{updateInfo?.version}
            </div>
          )}
          {(status === 'idle' || status === 'up-to-date' || status === 'error' || status === 'checking') && (
            <div className="text-body text-fg-secondary">
              {status === 'checking'
                ? 'Checking the update server…'
                : 'Check manually if auto-check has not triggered yet.'}
            </div>
          )}
        </div>

        <div className="shrink-0 flex gap-2">
          {/* Check button — available when not busy */}
          {(status === 'idle' || status === 'up-to-date' || status === 'error') && (
            <button
              type="button"
              onClick={check}
              className="inline-flex items-center gap-1.5 rounded-md border border-stroke-hairline/60 bg-bg-surface px-3 py-1.5 text-body text-fg-secondary transition-colors hover:border-stroke-hairline hover:text-fg-primary"
            >
              <Download size={13} strokeWidth={1.75} />
              Check for updates
            </button>
          )}
          {/* Download button */}
          {status === 'available' && (
            <button
              type="button"
              onClick={download}
              className="btn-brand inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-body font-medium transition-colors"
            >
              <Download size={13} strokeWidth={2} />
              Download
            </button>
          )}
          {/* Install button */}
          {status === 'ready' && (
            <button
              type="button"
              onClick={install}
              className="inline-flex items-center gap-1.5 rounded-md bg-green-600 px-3 py-1.5 text-body font-medium text-white transition-opacity hover:opacity-90"
            >
              Restart &amp; Install
            </button>
          )}
          {/* Checking spinner */}
          {status === 'checking' && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 text-body text-fg-tertiary">
              <Download size={13} strokeWidth={1.75} className="animate-pulse" />
              Checking…
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Connection section ───────────────────────────────────────────────────────

function ConnectionSection() {
  const { status, lastCheckedAt, recheck } = useBackendStatus();

  const statusPill: Record<typeof status, { label: string; cls: string }> = {
    checking: { label: 'Checking…',   cls: 'bg-fg-tertiary/10 text-fg-secondary' },
    online:   { label: 'Online',      cls: 'bg-green-500/10 text-green-700 dark:text-green-400' },
    offline:  { label: 'Unreachable', cls: 'bg-red-500/10 text-red-600' },
  };
  const pill = statusPill[status];

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <div className="mb-4">
          <h2 className="text-heading font-medium text-fg-primary">Backend Connection</h2>
          <p className="text-label text-fg-secondary">
            Live reachability check against the configured API server.
            The app continues to work offline using local WatermelonDB data.
          </p>
        </div>

        {/* API URL row */}
        <div className="flex items-start justify-between gap-4 border-t border-stroke-hairline/40 py-3">
          <div>
            <div className="text-body font-medium text-fg-primary">API server</div>
            <div className="mt-0.5 font-mono text-label text-fg-secondary break-all">{API_BASE_URL}</div>
          </div>
          <span
            className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-label font-medium ${pill.cls}`}
          >
            {pill.label}
          </span>
        </div>

        {/* Last checked row */}
        <div className="flex items-center justify-between border-t border-stroke-hairline/40 py-3">
          <div>
            <div className="text-body font-medium text-fg-primary">Last checked</div>
            <div className="text-label text-fg-secondary">
              {lastCheckedAt
                ? lastCheckedAt.toLocaleTimeString()
                : 'Not yet checked'}
            </div>
          </div>
          <button
            type="button"
            onClick={recheck}
            disabled={status === 'checking'}
            className="inline-flex items-center gap-1.5 rounded-md border border-stroke-hairline/60 bg-bg-surface px-3 py-1.5 text-body text-fg-secondary transition-colors hover:border-stroke-hairline hover:text-fg-primary disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Wifi size={13} strokeWidth={1.75} />
            {status === 'checking' ? 'Checking…' : 'Test connection'}
          </button>
        </div>
      </Card>

      {status === 'offline' && (
        <Card className="p-5 border-red-500/30 bg-red-500/5">
          <h3 className="mb-1 text-body font-medium text-red-600">Cannot reach backend</h3>
          <p className="text-label text-fg-secondary">
            Check that the BigxBook server is running and that{' '}
            <span className="font-mono">{API_BASE_URL}</span>{' '}
            is correct. Syncs will queue locally and retry automatically when the connection is restored.
          </p>
        </Card>
      )}
    </div>
  );
}

